import React, { useEffect, useMemo } from 'react';
import SectionCard from '../components/SectionCard.jsx';
import NestedSection from '../components/NestedSection.jsx';
import KPICard from '../components/KPICard.jsx';
import DataTable from '../components/DataTable.jsx';
import MoneyCell from '../components/MoneyCell.jsx';
import EduTip from '../components/EduTip.jsx';
/* Proposal 3 (2026-04-25) — Journal Entry editor moved to FCC. JEs are
   simulations and belong in the FCC sandbox alongside buy/sell + LTL sims.
   ActualsTab still SEES posted JEs in its formal statements (the engine
   applies them globally) — it just no longer authors them. */
import FormalStatementTable, { shapeIncomeStatement, shapeBalanceSheet, shapeCashFlowStatement, shapeIncomeStatementMulti, shapeBalanceSheetMulti, shapeCashFlowStatementMulti } from '../components/FormalStatementTable.jsx';
import FormalStatementSection from '../components/FormalStatementSection.jsx';
import { makeShowBlock, SubViewBreadcrumb } from '../components/SubViewShell.jsx';
import ComparativeTablesView from '../components/ComparativeTablesView.jsx';
import MetricHorizonTable from '../components/MetricHorizonTable.jsx';
import ModuleSettingsPanel from '../components/ModuleSettingsPanel.jsx';
import ModifiedBadge, { isAccountVisibilityCustomized, OVERRIDE_REASON } from '../components/ModifiedBadge.jsx';
import { BALANCES, FIXED_ASSETS, SALARY, MORTGAGES } from '../data.js';
import {
  fmtMoney, fmtPct, fmtDate, currentYM,
  totalAssetsLiquid, totalCreditCardDebt, totalCCCredits, totalLongTermLiabilities,
  totalEquity, totalSuspense, netWorth,
  calcMonthlyGross, calcMonthlyNet, calcMonthlyWHT, calcMonthlyGovContribs,
  calcFortnightlyGross, calcMonthlyMortgages,
  buildIncomeStatement, buildCashFlowSnapshot,
  recurringBreakdown,
  kpiSparkSeries,
  resolveAccountVisibility,
  totalFixedAssetsNet, totalCCDebtFromBlocks, totalCCCreditsFromBlocks, totalLTLFromBlocks,
  buildThreeStatementForDirection, getTabDirection,
  aggregateByDensity, fmtPeriod, normalizePeriodScalar,
  currentPeriodStateForRows,
  getModuleFeatures,
} from '../engine.js';
import { KPISparkline } from '../charts/index.jsx';

const TAB_CONTENT = 'flex flex-col [&>*+*]:mt-4';
const KPI_ROW = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mb-5';
const KPI_ROW_COMPACT = 'grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 mt-3 mb-0';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';

const DRIVER_ROW = 'grid grid-cols-[1fr_auto] gap-[0.6rem] items-center py-[0.35rem] border-b border-dashed border-border last:border-b-0 text-[0.85rem]';

const moneyCellCol = (key, extra = {}) => ({
  key,
  align: 'right',
  format: (v, row) => (
    <MoneyCell
      value={v}
      breakdown={extra.breakdown ? extra.breakdown(row) : undefined}
      term={extra.term}
      decimals={extra.decimals ?? 2}
      tone={extra.tone ? extra.tone(v, row) : undefined}
    />
  ),
});

const ledgerAmountCol = (key = 'amount') => ({
  key,
  align: 'right',
  format: (v) => <MoneyCell value={v} />,
});

function rowsForSection(rows, section) {
  return (Array.isArray(rows) ? rows : []).filter((row) => row.section === section);
}

function sumAmounts(rows) {
  return (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
}

function ledgerStatementLine(row, sign = 1) {
  return {
    label: row.name,
    amount: (Number(row.amount) || 0) * sign,
    currency: row.currency,
    indent: 1,
    moneyProvenance: row.moneyProvenance,
    __moneyProvenance: row.__moneyProvenance,
    __ledgerAmountBasis: row.__ledgerAmountBasis,
    __ledgerAmountCurrency: row.__ledgerAmountCurrency,
    __ledgerUnconvertedReportingCurrency: row.__ledgerUnconvertedReportingCurrency,
    __ledgerAggregateRow: row.__ledgerAggregateRow,
    __ledgerAggregateReportType: row.__ledgerAggregateReportType,
    __ledgerAggregateReportLabel: row.__ledgerAggregateReportLabel,
    __ledgerAggregateFormalLineItemsHeld: row.__ledgerAggregateFormalLineItemsHeld,
    __ledgerAggregateFormalLineItemHeldReason: row.__ledgerAggregateFormalLineItemHeldReason,
  };
}

function ledgerIncomeSections(actuals) {
  const rows = actuals?.incomeStatementRows || [];
  const revenueRows = rowsForSection(rows, 'revenue');
  const expenseRows = rowsForSection(rows, 'expenses');
  const revenue = actuals?.totals?.revenue ?? sumAmounts(revenueRows);
  const expenses = actuals?.totals?.expenses ?? sumAmounts(expenseRows);
  const netIncome = actuals?.totals?.netIncome ?? (revenue - expenses);
  return [
    {
      heading: 'Revenue',
      lines: revenueRows.map((row) => ledgerStatementLine(row)),
      total: { label: 'Total revenue', amount: revenue },
    },
    {
      heading: 'Expenses',
      lines: expenseRows.map((row) => ledgerStatementLine(row, -1)),
      total: { label: 'Total expenses', amount: -expenses, betterWhen: 'lower' },
    },
    {
      lines: [],
      total: { label: 'Net income', amount: netIncome, rule: true },
    },
  ];
}

function ledgerBalanceSections(actuals) {
  const rows = actuals?.balanceSheetRows || [];
  const assetRows = rowsForSection(rows, 'assets');
  const liabilityRows = rowsForSection(rows, 'liabilities');
  const equityRows = rowsForSection(rows, 'equity');
  const totalAssets = actuals?.totals?.totalAssets ?? sumAmounts(assetRows);
  const totalLiabilities = actuals?.totals?.totalLiabilities ?? sumAmounts(liabilityRows);
  const totalEquity = actuals?.totals?.equity ?? sumAmounts(equityRows);
  return [
    {
      heading: 'Assets',
      lines: assetRows.map((row) => ledgerStatementLine(row)),
      total: { label: 'Total assets', amount: totalAssets },
    },
    {
      heading: 'Liabilities',
      lines: liabilityRows.map((row) => ({ ...ledgerStatementLine(row), betterWhen: 'lower' })),
      total: { label: 'Total liabilities', amount: totalLiabilities, betterWhen: 'lower' },
    },
    {
      heading: 'Equity',
      lines: equityRows.map((row) => ledgerStatementLine(row)),
      total: { label: 'Total equity', amount: totalEquity },
    },
    {
      lines: [],
      total: { label: 'Total liabilities + equity', amount: totalLiabilities + totalEquity, rule: true, betterWhen: 'neutral' },
    },
  ];
}

function ledgerCashFlowSections(actuals) {
  const rows = actuals?.cashFlowRows || [];
  const operatingRows = rowsForSection(rows, 'operating');
  const investingRows = rowsForSection(rows, 'investing');
  const financingRows = rowsForSection(rows, 'financing');
  return [
    {
      heading: 'Operating activities',
      lines: operatingRows.map((row) => ledgerStatementLine(row)),
      total: { label: 'Net cash from operations (CFO)', amount: sumAmounts(operatingRows) },
    },
    {
      heading: 'Investing activities',
      lines: investingRows.map((row) => ledgerStatementLine(row)),
      total: { label: 'Net cash from investing (CFI)', amount: sumAmounts(investingRows) },
    },
    {
      heading: 'Financing activities',
      lines: financingRows.map((row) => ledgerStatementLine(row)),
      total: { label: 'Net cash from financing (CFF)', amount: sumAmounts(financingRows) },
    },
    {
      lines: [],
      total: { label: 'Net change in cash', amount: actuals?.totals?.netCashChange ?? sumAmounts(rows), rule: true },
    },
  ];
}

function amountNumberOrNull(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value.replaceAll(',', ''));
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value === 'object') {
    return amountNumberOrNull(
      value.amount
      ?? value.value
      ?? value.presentationAmount?.amount
      ?? value.signedAmount?.amount
      ?? value.adjusted?.signedAmount?.amount
    );
  }
  return null;
}

function amountNumber(value) {
  return amountNumberOrNull(value) ?? 0;
}

function rollupYM(row) {
  return String(row?.ym || row?.label || '').slice(0, 7);
}

function validYM(ym) {
  return /^\d{4}-\d{2}$/.test(String(ym || ''));
}

function monthEndISOFromYM(ym) {
  if (!validYM(ym)) return null;
  const [year, month] = ym.split('-').map(Number);
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] || 31;
  return `${ym}-${String(days).padStart(2, '0')}`;
}

function ledgerRollupHas(row, keys) {
  return keys.some((key) => amountNumberOrNull(row?.[key]) != null);
}

function ledgerRollupOptional(row, keys) {
  for (const key of keys) {
    const value = amountNumberOrNull(row?.[key]);
    if (value != null) return value;
  }
  return null;
}

function ledgerRollupValue(row, keys, fallback = 0) {
  return ledgerRollupOptional(row, keys) ?? fallback;
}

function ledgerRollupRevenue(row) {
  const direct = ledgerRollupOptional(row, ['totalRevenue', 'revenue', 'grossRevenue', 'incomeTotal']);
  if (direct != null) return direct;
  return ['salaryIncome', 'primaryIncome', 'recurringRevenue', 'fvfRevenue', 'income']
    .reduce((sum, key) => sum + amountNumber(row?.[key]), 0);
}

function ledgerRollupExpenses(row) {
  const direct = ledgerRollupOptional(row, ['totalExpenses', 'expenses', 'expenseTotal']);
  return direct != null ? Math.abs(direct) : 0;
}

function ledgerRollupNetIncome(row) {
  const direct = ledgerRollupOptional(row, ['netIncome', 'netEarnings', 'netResult']);
  if (direct != null) return direct;
  const hasRevenue = ledgerRollupHas(row, ['totalRevenue', 'revenue', 'grossRevenue', 'incomeTotal', 'salaryIncome', 'primaryIncome', 'recurringRevenue', 'fvfRevenue', 'income']);
  const hasExpenses = ledgerRollupHas(row, ['totalExpenses', 'expenses', 'expenseTotal']);
  return hasRevenue && hasExpenses ? ledgerRollupRevenue(row) - ledgerRollupExpenses(row) : 0;
}

function ledgerRollupNetCashChange(row) {
  return ledgerRollupValue(row, ['netCashChange', 'netFCF', 'freeCashFlow', 'cashFlow'], 0);
}

function ledgerRollupLiquid(row) {
  return ledgerRollupValue(row, ['liquidEnding', 'assetsLiquid', 'cashEnding', 'cashEndingBalance', 'cash'], 0);
}

function ledgerRollupShortDebt(row) {
  return ledgerRollupValue(row, ['ccDebtEnding', 'ccDebt', 'shortTermDebt', 'currentDebt'], 0);
}

function ledgerRollupLongDebt(row) {
  return ledgerRollupValue(row, ['ltl', 'longTermLiabilities', 'longTermDebt'], 0);
}

function ledgerRollupFixedAssets(row) {
  return ledgerRollupValue(row, ['fixedAssetsNet', 'nonCurrentAssets', 'ncAssets'], 0);
}

function ledgerRollupTotalAssets(row) {
  const direct = ledgerRollupOptional(row, ['totalAssets', 'assetsEnding', 'assets']);
  return direct ?? (ledgerRollupLiquid(row) + ledgerRollupFixedAssets(row));
}

function ledgerRollupTotalLiabilities(row) {
  const direct = ledgerRollupOptional(row, ['totalLiabilities', 'liabilitiesEnding', 'liabilities']);
  return direct ?? (ledgerRollupShortDebt(row) + ledgerRollupLongDebt(row));
}

function ledgerRollupEquity(row) {
  const direct = ledgerRollupOptional(row, ['totalEquity', 'equity', 'equityEnding']);
  return direct ?? (ledgerRollupTotalAssets(row) - ledgerRollupTotalLiabilities(row));
}

function ledgerRollupCFO(row) {
  return ledgerRollupValue(row, ['cfo', 'netCashFromOperations', 'operatingCashFlow'], 0);
}

function ledgerRollupCFI(row) {
  return ledgerRollupValue(row, ['cfi', 'netCashFromInvesting', 'investingCashFlow'], 0);
}

function ledgerRollupCFF(row) {
  return ledgerRollupValue(row, ['cff', 'netCashFromFinancing', 'financingCashFlow'], 0);
}

function filterLedgerRollupsForRange(rollups, range) {
  const startDate = range?.startDate || range?.minDate || null;
  const endDate = range?.endDate || range?.maxDate || null;
  return (Array.isArray(rollups) ? rollups : [])
    .map((row) => ({ ...row, ym: rollupYM(row) }))
    .filter((row) => validYM(row.ym))
    .filter((row) => {
      const monthStart = `${row.ym}-01`;
      const monthEnd = monthEndISOFromYM(row.ym);
      if (startDate && monthEnd && monthEnd < startDate) return false;
      if (endDate && monthStart > endDate) return false;
      return true;
    })
    .sort((a, b) => a.ym.localeCompare(b.ym));
}

const LEDGER_ROLLUP_STOCK_KEYS = new Set([
  'liquidEnding', 'assetsLiquid', 'cashEnding', 'cashEndingBalance', 'cash',
  'ccDebtEnding', 'ccDebt', 'shortTermDebt', 'currentDebt',
  'ltl', 'longTermLiabilities', 'longTermDebt',
  'fixedAssetsNet', 'nonCurrentAssets', 'ncAssets',
  'totalAssets', 'assetsEnding', 'assets',
  'totalLiabilities', 'liabilitiesEnding', 'liabilities',
  'totalEquity', 'equity', 'equityEnding',
]);

function aggregateLedgerRollups(rows, density) {
  const d = Math.max(1, Math.floor(density || 1));
  if (d <= 1) return rows.map((row) => ({ ...row, ym: rollupYM(row), label: rollupYM(row) }));
  const out = [];
  for (let i = 0; i < rows.length; i += d) {
    const window = rows.slice(i, i + d);
    if (window.length === 0) continue;
    const last = window[window.length - 1];
    const merged = { ...last, ym: rollupYM(last), label: rollupYM(last), __sourcePeriodCount: window.length };
    const keys = new Set(window.flatMap((row) => Object.keys(row || {})));
    for (const key of keys) {
      const values = window.map((row) => amountNumberOrNull(row?.[key])).filter((value) => value != null);
      if (values.length === 0) continue;
      merged[key] = LEDGER_ROLLUP_STOCK_KEYS.has(key)
        ? values[values.length - 1]
        : values.reduce((sum, value) => sum + value, 0);
    }
    out.push(merged);
  }
  return out;
}

function buildLedgerActualsPeriodBuckets({ rollups, scenario, periodCount, density }) {
  const range = scenario?._actualsDateRange || scenario?.reportingDateRange || null;
  const ranged = filterLedgerRollupsForRange(rollups, range);
  const bucketed = aggregateLedgerRollups(ranged, density);
  const rows = bucketed.slice(-Math.max(1, periodCount || 1));
  const periodLabels = rows.map((row) => fmtPeriod(row.ym ?? row.label, density));
  const currentState = currentPeriodStateForRows(rows, density);
  const periodLabel = periodLabels.length > 1
    ? `${periodLabels[0]} - ${periodLabels[periodLabels.length - 1]}`
    : periodLabels[0] || null;
  return {
    rows,
    periodLabels,
    currentState,
    currentLabel: currentState.label,
    periodLabel,
  };
}

function ledgerIncomeSectionsMulti(rows) {
  return [
    {
      heading: 'Revenue',
      lines: [{ label: 'Ledger revenue', amounts: rows.map(ledgerRollupRevenue), indent: 1 }],
      total: { label: 'Total revenue', amounts: rows.map(ledgerRollupRevenue) },
    },
    {
      heading: 'Expenses',
      lines: [{ label: 'Ledger expenses', amounts: rows.map((row) => -ledgerRollupExpenses(row)), indent: 1, betterWhen: 'lower' }],
      total: { label: 'Total expenses', amounts: rows.map((row) => -ledgerRollupExpenses(row)), betterWhen: 'lower' },
    },
    {
      lines: [],
      total: { label: 'Net income', amounts: rows.map(ledgerRollupNetIncome), rule: true },
    },
  ];
}

function ledgerIncomeSectionsFromRollup(row) {
  const revenue = ledgerRollupRevenue(row);
  const expenses = ledgerRollupExpenses(row);
  const netIncome = ledgerRollupNetIncome(row);
  return [
    {
      heading: 'Revenue',
      lines: [{ label: 'Ledger revenue', amount: revenue, indent: 1 }],
      total: { label: 'Total revenue', amount: revenue },
    },
    {
      heading: 'Expenses',
      lines: [{ label: 'Ledger expenses', amount: -expenses, indent: 1, betterWhen: 'lower' }],
      total: { label: 'Total expenses', amount: -expenses, betterWhen: 'lower' },
    },
    {
      lines: [],
      total: { label: 'Net income', amount: netIncome, rule: true },
    },
  ];
}

function ledgerBalanceSectionsMulti(rows) {
  return [
    {
      heading: 'Assets',
      lines: [
        { label: 'Liquid assets', amounts: rows.map(ledgerRollupLiquid), indent: 1 },
        { label: 'Non-current assets', amounts: rows.map(ledgerRollupFixedAssets), indent: 1 },
      ],
      total: { label: 'Total assets', amounts: rows.map(ledgerRollupTotalAssets) },
    },
    {
      heading: 'Liabilities',
      lines: [
        { label: 'Short-term debt', amounts: rows.map(ledgerRollupShortDebt), indent: 1, betterWhen: 'lower' },
        { label: 'Long-term liabilities', amounts: rows.map(ledgerRollupLongDebt), indent: 1, betterWhen: 'lower' },
      ],
      total: { label: 'Total liabilities', amounts: rows.map(ledgerRollupTotalLiabilities), betterWhen: 'lower' },
    },
    {
      heading: 'Equity',
      lines: [{ label: 'Total equity (A - L)', amounts: rows.map(ledgerRollupEquity), indent: 1 }],
      total: { label: 'Total equity', amounts: rows.map(ledgerRollupEquity) },
    },
    {
      lines: [],
      total: {
        label: 'Total liabilities + equity',
        amounts: rows.map((row) => ledgerRollupTotalLiabilities(row) + ledgerRollupEquity(row)),
        rule: true,
        betterWhen: 'neutral',
      },
    },
  ];
}

function ledgerBalanceSectionsFromRollup(row) {
  const totalAssets = ledgerRollupTotalAssets(row);
  const totalLiabilities = ledgerRollupTotalLiabilities(row);
  const equity = ledgerRollupEquity(row);
  return [
    {
      heading: 'Assets',
      lines: [
        { label: 'Liquid assets', amount: ledgerRollupLiquid(row), indent: 1 },
        { label: 'Non-current assets', amount: ledgerRollupFixedAssets(row), indent: 1 },
      ],
      total: { label: 'Total assets', amount: totalAssets },
    },
    {
      heading: 'Liabilities',
      lines: [
        { label: 'Short-term debt', amount: ledgerRollupShortDebt(row), indent: 1, betterWhen: 'lower' },
        { label: 'Long-term liabilities', amount: ledgerRollupLongDebt(row), indent: 1, betterWhen: 'lower' },
      ],
      total: { label: 'Total liabilities', amount: totalLiabilities, betterWhen: 'lower' },
    },
    {
      heading: 'Equity',
      lines: [{ label: 'Total equity (A - L)', amount: equity, indent: 1 }],
      total: { label: 'Total equity', amount: equity },
    },
    {
      lines: [],
      total: {
        label: 'Total liabilities + equity',
        amount: totalLiabilities + equity,
        rule: true,
        betterWhen: 'neutral',
      },
    },
  ];
}

function ledgerCashFlowSectionsMulti(rows) {
  return [
    {
      heading: 'Operating activities',
      lines: [{ label: 'Ledger CFO', amounts: rows.map(ledgerRollupCFO), indent: 1 }],
      total: { label: 'Net cash from operations (CFO)', amounts: rows.map(ledgerRollupCFO) },
    },
    {
      heading: 'Investing activities',
      lines: [{ label: 'Ledger CFI', amounts: rows.map(ledgerRollupCFI), indent: 1 }],
      total: { label: 'Net cash from investing (CFI)', amounts: rows.map(ledgerRollupCFI) },
    },
    {
      heading: 'Financing activities',
      lines: [{ label: 'Ledger CFF', amounts: rows.map(ledgerRollupCFF), indent: 1 }],
      total: { label: 'Net cash from financing (CFF)', amounts: rows.map(ledgerRollupCFF) },
    },
    {
      lines: [],
      total: { label: 'Net change in cash', amounts: rows.map(ledgerRollupNetCashChange), rule: true },
    },
  ];
}

function ledgerCashFlowSectionsFromRollup(row) {
  const cfo = ledgerRollupCFO(row);
  const cfi = ledgerRollupCFI(row);
  const cff = ledgerRollupCFF(row);
  return [
    {
      heading: 'Operating activities',
      lines: [{ label: 'Ledger CFO', amount: cfo, indent: 1 }],
      total: { label: 'Net cash from operations (CFO)', amount: cfo },
    },
    {
      heading: 'Investing activities',
      lines: [{ label: 'Ledger CFI', amount: cfi, indent: 1 }],
      total: { label: 'Net cash from investing (CFI)', amount: cfi },
    },
    {
      heading: 'Financing activities',
      lines: [{ label: 'Ledger CFF', amount: cff, indent: 1 }],
      total: { label: 'Net cash from financing (CFF)', amount: cff },
    },
    {
      lines: [],
      total: { label: 'Net change in cash', amount: ledgerRollupNetCashChange(row), rule: true },
    },
  ];
}

function buildLedgerWindowTotals(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const last = safeRows[safeRows.length - 1] || null;
  const revenue = safeRows.reduce((sum, row) => sum + ledgerRollupRevenue(row), 0);
  const expenses = safeRows.reduce((sum, row) => sum + ledgerRollupExpenses(row), 0);
  const netIncome = safeRows.reduce((sum, row) => sum + ledgerRollupNetIncome(row), 0);
  const netCashChange = safeRows.reduce((sum, row) => sum + ledgerRollupNetCashChange(row), 0);
  const totalAssets = last ? ledgerRollupTotalAssets(last) : 0;
  const totalLiabilities = last ? ledgerRollupTotalLiabilities(last) : 0;
  const equity = last ? ledgerRollupEquity(last) : totalAssets - totalLiabilities;
  const assetsLiquid = last ? ledgerRollupLiquid(last) : 0;
  const ccDebt = last ? ledgerRollupShortDebt(last) : 0;
  const ltl = last ? ledgerRollupLongDebt(last) : Math.max(0, totalLiabilities - ccDebt);
  const fixedAssetsNet = last ? ledgerRollupFixedAssets(last) : Math.max(0, totalAssets - assetsLiquid);
  return {
    assetsLiquid,
    fixedAssetsNet,
    totalAssets,
    ccDebt,
    ccCredits: 0,
    ltl,
    totalLiabilities,
    equity,
    susTotal: 0,
    netWorthProxy: totalAssets - totalLiabilities,
    revenue,
    expenses,
    netIncome,
    netCashChange,
  };
}

function ledgerRollupTableRow(name, cat, section, amount, extra = {}) {
  return {
    name,
    cat,
    section,
    amount,
    source: 'ledger-rollup',
    rowAuthorship: 'ledger-rollup-selected-window',
    ...extra,
  };
}

function ledgerBalanceAssetRowsFromRollupWindow(rows) {
  const totals = buildLedgerWindowTotals(rows);
  return [
    ledgerRollupTableRow('Liquid assets', 'Assets', 'assets', totals.assetsLiquid),
    ledgerRollupTableRow('Non-current assets', 'Assets', 'assets', totals.fixedAssetsNet),
  ];
}

function ledgerBalanceLiabilityRowsFromRollupWindow(rows) {
  const totals = buildLedgerWindowTotals(rows);
  return [
    ledgerRollupTableRow('Short-term debt', 'Liabilities', 'liabilities', totals.ccDebt),
    ledgerRollupTableRow('Long-term liabilities', 'Liabilities', 'liabilities', totals.ltl),
  ];
}

function ledgerEquityRowsFromRollupWindow(rows) {
  const totals = buildLedgerWindowTotals(rows);
  return [
    ledgerRollupTableRow('Total equity (A - L)', 'Equity', 'equity', totals.equity),
  ];
}

function ledgerIncomeRowsFromRollupWindow(rows) {
  const totals = buildLedgerWindowTotals(rows);
  return [
    ledgerRollupTableRow('Ledger revenue', 'Revenue', 'revenue', totals.revenue),
    ledgerRollupTableRow('Ledger expenses', 'Expenses', 'expenses', -totals.expenses, { betterWhen: 'lower' }),
  ];
}

function ledgerCashFlowRowsFromRollupWindow(rows) {
  const totals = buildLedgerWindowTotals(rows);
  return [
    ledgerRollupTableRow('Net cash movement', 'Cash Flow', 'cash-flow', totals.netCashChange),
  ];
}

function ledgerComparativeRows(rows) {
  return [
    { ratio: 'Revenue', category: 'Income Statement', format: 'currency', betterWhen: 'higher', values: rows.map(ledgerRollupRevenue) },
    { ratio: 'Net income', category: 'Income Statement', format: 'currency', betterWhen: 'higher', values: rows.map(ledgerRollupNetIncome) },
    { ratio: 'Liquid ending', category: 'Balance Sheet', format: 'currency', betterWhen: 'higher', values: rows.map(ledgerRollupLiquid) },
    { ratio: 'Short-term debt', category: 'Balance Sheet', format: 'currency', betterWhen: 'lower', values: rows.map(ledgerRollupShortDebt) },
    { ratio: 'Total assets', category: 'Balance Sheet', format: 'currency', betterWhen: 'higher', values: rows.map(ledgerRollupTotalAssets) },
    { ratio: 'Total liabilities', category: 'Balance Sheet', format: 'currency', betterWhen: 'lower', values: rows.map(ledgerRollupTotalLiabilities) },
    { ratio: 'Equity', category: 'Balance Sheet', format: 'currency', betterWhen: 'higher', values: rows.map(ledgerRollupEquity) },
    { ratio: 'Net cash movement', category: 'Cash Flow', format: 'currency', betterWhen: 'higher', values: rows.map(ledgerRollupNetCashChange) },
  ];
}

const HELD_ACTUALS_PERIODS = Object.freeze(['Held period 1', 'Held period 2', 'Held period 3']);

function heldSeries(periods = HELD_ACTUALS_PERIODS) {
  return periods.map(() => 0);
}

function heldIncomeSections(periods = HELD_ACTUALS_PERIODS) {
  const zeros = heldSeries(periods);
  return [
    {
      heading: 'Revenue',
      lines: [{ label: 'Ledger revenue held', amounts: zeros, indent: 1 }],
      total: { label: 'Total revenue', amounts: zeros },
    },
    {
      heading: 'Expenses',
      lines: [{ label: 'Ledger expenses held', amounts: zeros, indent: 1, betterWhen: 'lower' }],
      total: { label: 'Total expenses', amounts: zeros, betterWhen: 'lower' },
    },
    {
      lines: [],
      total: { label: 'Net income', amounts: zeros, rule: true },
    },
  ];
}

function heldBalanceSections(periods = HELD_ACTUALS_PERIODS) {
  const zeros = heldSeries(periods);
  return [
    {
      heading: 'Assets',
      lines: [{ label: 'Ledger assets held', amounts: zeros, indent: 1 }],
      total: { label: 'Total assets', amounts: zeros },
    },
    {
      heading: 'Liabilities',
      lines: [{ label: 'Ledger liabilities held', amounts: zeros, indent: 1, betterWhen: 'lower' }],
      total: { label: 'Total liabilities', amounts: zeros, betterWhen: 'lower' },
    },
    {
      heading: 'Equity',
      lines: [{ label: 'Ledger equity held', amounts: zeros, indent: 1 }],
      total: { label: 'Total equity', amounts: zeros },
    },
    {
      lines: [],
      total: { label: 'Total liabilities + equity', amounts: zeros, rule: true, betterWhen: 'neutral' },
    },
  ];
}

function heldCashFlowSections(periods = HELD_ACTUALS_PERIODS) {
  const zeros = heldSeries(periods);
  return [
    {
      heading: 'Operating activities',
      lines: [{ label: 'Ledger CFO held', amounts: zeros, indent: 1 }],
      total: { label: 'Net cash from operations (CFO)', amounts: zeros },
    },
    {
      heading: 'Investing activities',
      lines: [{ label: 'Ledger CFI held', amounts: zeros, indent: 1 }],
      total: { label: 'Net cash from investing (CFI)', amounts: zeros },
    },
    {
      heading: 'Financing activities',
      lines: [{ label: 'Ledger CFF held', amounts: zeros, indent: 1 }],
      total: { label: 'Net cash from financing (CFF)', amounts: zeros },
    },
    {
      lines: [],
      total: { label: 'Net change in cash', amounts: zeros, rule: true },
    },
  ];
}

function heldComparativeRows(periods = HELD_ACTUALS_PERIODS) {
  const zeros = heldSeries(periods);
  return [
    { ratio: 'Revenue', category: 'Income Statement', format: 'currency', betterWhen: 'higher', values: zeros },
    { ratio: 'Net income', category: 'Income Statement', format: 'currency', betterWhen: 'higher', values: zeros },
    { ratio: 'Total assets', category: 'Balance Sheet', format: 'currency', betterWhen: 'higher', values: zeros },
    { ratio: 'Total liabilities', category: 'Balance Sheet', format: 'currency', betterWhen: 'lower', values: zeros },
    { ratio: 'Equity', category: 'Balance Sheet', format: 'currency', betterWhen: 'higher', values: zeros },
    { ratio: 'Net cash movement', category: 'Cash Flow', format: 'currency', betterWhen: 'higher', values: zeros },
  ];
}

function LedgerRowsTable({ rows, footerLabel, footerAmount }) {
  return (
    <DataTable
      compact
      columns={[
        { key: 'name', label: 'Ledger row', width: '46%' },
        { key: 'cat', label: 'Section', width: '18%' },
        { ...ledgerAmountCol('amount'), label: 'Amount', width: '20%' },
        { key: 'rowAuthorship', label: 'Proof' },
      ]}
      rows={rows}
      footer={footerLabel ? { name: footerLabel, cat: '', amount: footerAmount, rowAuthorship: 'ledger-derived' } : undefined}
      pagination
      pageSize={25}
      paginationLabel="Ledger aggregate rows"
    />
  );
}

function HeldEquitySection({ subtitle, proof = 'scope-hold' }) {
  const rows = [
    ledgerRollupTableRow('Total equity (A - L)', 'Equity', 'equity', 0, {
      source: proof,
      rowAuthorship: proof,
    }),
  ];
  return (
    <SectionCard sectionId="equity" title="Equity" subtitle={subtitle}>
      <LedgerRowsTable rows={rows} footerLabel="Total equity" footerAmount={0} />
    </SectionCard>
  );
}

function HeldFormalStatements({ subtitle, sourceState }) {
  return (
    <>
      <SectionCard sectionId="formal" title="Income Statement (formal)" subtitle={subtitle}>
        <FormalStatementTable
          title="Statement of Income"
          entity="Selected Ledger Scope"
          period="Held until Ledger scope is selected"
          periods={HELD_ACTUALS_PERIODS}
          sections={heldIncomeSections()}
          sourceState={sourceState}
        />
      </SectionCard>
      <SectionCard title="Balance Sheet (formal)" subtitle={`${subtitle} Includes equity; all held values remain zero.`}>
        <FormalStatementTable
          title="Statement of Financial Position"
          entity="Selected Ledger Scope"
          period="Held until Ledger scope is selected"
          periods={HELD_ACTUALS_PERIODS}
          sections={heldBalanceSections()}
          sourceState={sourceState}
        />
      </SectionCard>
      <SectionCard title="Cash Flow Statement (formal)" subtitle={subtitle}>
        <FormalStatementTable
          title="Statement of Cash Flows"
          entity="Selected Ledger Scope"
          period="Held until Ledger scope is selected"
          periods={HELD_ACTUALS_PERIODS}
          sections={heldCashFlowSections()}
          sourceState={sourceState}
        />
      </SectionCard>
    </>
  );
}

function HeldComparativeSection({ subtitle }) {
  return (
    <SectionCard sectionId="comparative" title="Comparative Tables" subtitle={subtitle}>
      <MetricHorizonTable
        title="Actuals metrics held until Ledger scope is selected"
        entity="Selected Ledger Scope"
        subtitle="No Book or Shelf scope; all values remain zero."
        period="Held zero shell"
        periods={HELD_ACTUALS_PERIODS}
        rows={heldComparativeRows()}
        showSparkline={false}
        showRowSparkline={false}
        sparkIdPrefix="actuals-held-zero"
      />
    </SectionCard>
  );
}

function LedgerActualsSurface({ scenario, setScenario, activeSection, onBackToOverview }) {
  const actuals = scenario?._ledgerActuals || {};
  const sourceState = scenario?._ledgerSourceState || null;
  const totals = actuals.totals || {};
  const rollups = Array.isArray(scenario?._ledgerRollups) ? scenario._ledgerRollups : [];
  const currency = actuals.currency || 'book currency';
  const actualsPeriods = normalizePeriodScalar(scenario.formalPeriodCount, 12);
  const actualsDensity = normalizePeriodScalar(scenario.formalPeriodDensity, 1);
  const actualsDirection = getTabDirection(scenario, 'actuals');
  const actualsDirLabel = actualsDirection === 'past' ? 'trailing' : actualsDirection === 'future' ? 'leading' : 'spanning';
  const ledgerBuckets = useMemo(
    () => buildLedgerActualsPeriodBuckets({
      rollups,
      scenario,
      periodCount: actualsPeriods,
      density: actualsDensity,
    }),
    [rollups, scenario, actualsPeriods, actualsDensity],
  );
  const ledgerPeriodLabels = ledgerBuckets.periodLabels;
  const ledgerIsMulti = ledgerPeriodLabels.length > 1;
  const ledgerPeriodLabel = ledgerBuckets.periodLabel;
  const ledgerRows = ledgerBuckets.rows;
  const ledgerHasWindowRows = ledgerRows.length > 0;
  const headlineTotals = ledgerHasWindowRows ? buildLedgerWindowTotals(ledgerRows) : totals;
  const ledgerControlSummary = ledgerIsMulti
    ? `${ledgerPeriodLabels.length}-period ${actualsDirLabel} Ledger rollup buckets (${actualsDensity}-month density)`
    : ledgerHasWindowRows
      ? 'Selected Ledger rollup bucket'
      : 'Ledger service aggregate rows';
  const periodLabel = actuals.coverageEnd
    ? `Through ${actuals.coverageEnd}`
    : actuals.asOfDate
      ? `As of ${actuals.asOfDate}`
      : 'Selected Ledger scope';
  const actualsWindowLabel = ledgerPeriodLabel || periodLabel;
  const assetRows = rowsForSection(actuals.balanceSheetRows, 'assets');
  const liabilityRows = rowsForSection(actuals.balanceSheetRows, 'liabilities');
  const equityRows = rowsForSection(actuals.balanceSheetRows, 'equity');
  const revenueRows = rowsForSection(actuals.incomeStatementRows, 'revenue');
  const expenseRows = rowsForSection(actuals.incomeStatementRows, 'expenses');
  const cashFlowRows = actuals.cashFlowRows || [];
  const selectedAssetRows = ledgerHasWindowRows ? ledgerBalanceAssetRowsFromRollupWindow(ledgerRows) : assetRows;
  const selectedLiabilityRows = ledgerHasWindowRows ? ledgerBalanceLiabilityRowsFromRollupWindow(ledgerRows) : liabilityRows;
  const selectedEquityRows = ledgerHasWindowRows ? ledgerEquityRowsFromRollupWindow(ledgerRows) : equityRows;
  const selectedIncomeRows = ledgerHasWindowRows ? ledgerIncomeRowsFromRollupWindow(ledgerRows) : [...revenueRows, ...expenseRows];
  const selectedRevenueRows = ledgerHasWindowRows
    ? selectedIncomeRows.filter((row) => row.section === 'revenue')
    : revenueRows;

  if (activeSection === 'comparative') {
    const comparisonRows = ledgerComparativeRows(ledgerRows);
    return (
      <div className={TAB_CONTENT}>
        <SubViewBreadcrumb moduleId="actuals" activeSection={activeSection} onBack={onBackToOverview} />
        <SectionCard
          sectionId="comparative"
          title="Comparative Tables"
          subtitle={`${ledgerControlSummary}. Selected Book or Shelf scope only; no static or synthetic fill.`}
        >
          {ledgerRows.length > 0 ? (
            <MetricHorizonTable
              title="Actuals ledger metrics across selected periods"
              entity="Selected Ledger Scope"
              subtitle="Ledger monthly rollups"
              period={ledgerPeriodLabel || periodLabel}
              periods={ledgerPeriodLabels}
              currentPeriodLabel={ledgerBuckets.currentLabel}
              currentPeriodProof={ledgerBuckets.currentState}
              rows={comparisonRows}
              showSparkline={scenario?.showTrendColumn !== false}
              showRowSparkline={scenario?.showRowSparkline !== false}
              gradient={!!scenario?.sparklineGradient}
              semanticTone={!!scenario?.sparklineSemanticTone}
              sparkIdPrefix="actuals-ledger-rollups"
            />
          ) : (
            <DataTable
              compact
              columns={[
                { key: 'ym', label: 'Period', width: '18%' },
                { ...ledgerAmountCol('liquidEnding'), label: 'Liquid ending', width: '20%' },
                { ...ledgerAmountCol('ccDebtEnding'), label: 'Short-term debt', width: '20%' },
                { ...ledgerAmountCol('netFCF'), label: 'Net cash flow', width: '20%' },
                { key: 'entriesInMonth', label: 'Entries' },
              ]}
              rows={ledgerRows}
            />
          )}
        </SectionCard>
      </div>
    );
  }

  return (
    <div className={TAB_CONTENT} data-ux-pattern="lens-ledger-scoped-actuals">
      <SubViewBreadcrumb moduleId="actuals" activeSection={activeSection} onBack={onBackToOverview} />

      {activeSection ? null : (
        <div className={KPI_ROW}>
          <KPICard
            label="Total Assets"
            value={<MoneyCell value={headlineTotals.totalAssets || 0} label="Total assets" />}
            sub={`${ledgerHasWindowRows ? `${ledgerRows.length} selected buckets` : `${assetRows.length} Ledger rows`} · ${currency}`}
            provenance="ledger"
            provenanceTip="Derived only from Ledger contract rows or selected reporting-window rollups for the selected Book or Shelf."
          />
          <KPICard
            label="Total Liabilities"
            value={<MoneyCell value={headlineTotals.totalLiabilities || 0} tone="bad" label="Total liabilities" />}
            sub={ledgerHasWindowRows ? actualsWindowLabel : `${liabilityRows.length} Ledger rows`}
            tone="bad"
            provenance="ledger"
            provenanceTip="Derived only from Ledger contract rows or selected reporting-window rollups for the selected Book or Shelf."
          />
          <KPICard
            label="Total Equity"
            value={<MoneyCell value={headlineTotals.equity || 0} label="Total equity" />}
            sub={ledgerHasWindowRows ? actualsWindowLabel : `${equityRows.length} Ledger rows`}
            provenance="ledger"
            provenanceTip="Derived only from Ledger contract rows or selected reporting-window rollups for the selected Book or Shelf."
          />
          <KPICard
            label="Net Income"
            value={<MoneyCell value={headlineTotals.netIncome || 0} tone={(headlineTotals.netIncome || 0) >= 0 ? 'good' : 'bad'} label="Net income" />}
            sub={ledgerHasWindowRows ? actualsWindowLabel : `${revenueRows.length + expenseRows.length} income statement rows`}
            provenance="ledger"
            provenanceTip="Revenue minus expense rows emitted by Ledger report-line authorship, or summed from the selected reporting-window rollups."
          />
          <KPICard
            label="Liquid Assets"
            value={<MoneyCell value={headlineTotals.assetsLiquid || 0} label="Liquid assets" />}
            sub="cash/bank-classified Ledger rows"
            provenance="ledger"
            provenanceTip="Cash and bank rows classified from Ledger account metadata."
          />
          <KPICard
            label="Net Cash Change"
            value={<MoneyCell value={headlineTotals.netCashChange || 0} label="Net cash change" />}
            sub={ledgerHasWindowRows ? actualsWindowLabel : `${cashFlowRows.length} cash-flow rows`}
            provenance="ledger"
            provenanceTip="Sum of Ledger cash-flow aggregate rows or selected reporting-window rollups for the selected scope."
          />
        </div>
      )}

      {(activeSection === undefined || activeSection === null || activeSection === 'assets') && (
        <SectionCard sectionId="assets" title="Assets" subtitle={`${actualsWindowLabel} · Ledger balance-sheet aggregate rows`}>
          <LedgerRowsTable rows={selectedAssetRows} footerLabel="Total assets" footerAmount={headlineTotals.totalAssets || 0} />
        </SectionCard>
      )}

      {(!activeSection || activeSection === 'liabilities') && (
        <SectionCard sectionId="liabilities" title="Liabilities" subtitle={`${actualsWindowLabel} · Ledger balance-sheet aggregate rows`}>
          <LedgerRowsTable rows={selectedLiabilityRows} footerLabel="Total liabilities" footerAmount={headlineTotals.totalLiabilities || 0} />
        </SectionCard>
      )}

      {(!activeSection || activeSection === 'equity') && (
        <SectionCard sectionId="equity" title="Equity" subtitle={`${actualsWindowLabel} · Ledger balance-sheet aggregate rows`}>
          <LedgerRowsTable rows={selectedEquityRows} footerLabel="Total equity" footerAmount={headlineTotals.equity || 0} />
        </SectionCard>
      )}

      {(!activeSection || activeSection === 'income') && (
        <SectionCard sectionId="income" title="Income Statement" subtitle={`${actualsWindowLabel} · Ledger report-line authorship`}>
          <LedgerRowsTable rows={selectedIncomeRows} footerLabel="Net income" footerAmount={headlineTotals.netIncome || 0} />
        </SectionCard>
      )}

      {(!activeSection || activeSection === 'primary-income') && (
        <SectionCard sectionId="primary-income" title="Revenue Detail" subtitle="Ledger income rows for the selected scope">
          <LedgerRowsTable rows={selectedRevenueRows} footerLabel="Total revenue" footerAmount={headlineTotals.revenue || 0} />
        </SectionCard>
      )}

      {(!activeSection || activeSection === 'formal') && (
        <>
          <FormalStatementSection
            tabId="actuals"
            stmtKey="is"
            label="Income Statement (formal)"
            subtitle={`${actualsWindowLabel} · ${ledgerControlSummary}`}
            title="Statement of Income"
            entity="Selected Ledger Scope"
            period={actualsWindowLabel}
            periods={ledgerIsMulti ? ledgerPeriodLabels : undefined}
            sections={ledgerHasWindowRows ? (ledgerIsMulti ? ledgerIncomeSectionsMulti(ledgerRows) : ledgerIncomeSectionsFromRollup(ledgerRows[0])) : ledgerIncomeSections(actuals)}
            currentPeriodLabel={ledgerBuckets.currentLabel}
            currentPeriodProof={ledgerBuckets.currentState}
            sourceState={sourceState}
            ledgerAggregateRowProof
            scenario={scenario}
            setScenario={setScenario}
            forceOpen={activeSection === 'formal'}
          />
          <FormalStatementSection
            tabId="actuals"
            stmtKey="bs"
            label="Balance Sheet (formal)"
            subtitle={`${actualsWindowLabel} · ${ledgerControlSummary} · includes equity`}
            title="Statement of Financial Position"
            entity="Selected Ledger Scope"
            period={actualsWindowLabel}
            periods={ledgerIsMulti ? ledgerPeriodLabels : undefined}
            sections={ledgerHasWindowRows ? (ledgerIsMulti ? ledgerBalanceSectionsMulti(ledgerRows) : ledgerBalanceSectionsFromRollup(ledgerRows[0])) : ledgerBalanceSections(actuals)}
            currentPeriodLabel={ledgerBuckets.currentLabel}
            currentPeriodProof={ledgerBuckets.currentState}
            sourceState={sourceState}
            ledgerAggregateRowProof
            scenario={scenario}
            setScenario={setScenario}
            forceOpen={activeSection === 'formal'}
          />
          <FormalStatementSection
            tabId="actuals"
            stmtKey="cf"
            label="Cash Flow Statement (formal)"
            subtitle={`${actualsWindowLabel} · ${ledgerControlSummary}`}
            title="Statement of Cash Flows"
            entity="Selected Ledger Scope"
            period={actualsWindowLabel}
            periods={ledgerIsMulti ? ledgerPeriodLabels : undefined}
            sections={ledgerHasWindowRows ? (ledgerIsMulti ? ledgerCashFlowSectionsMulti(ledgerRows) : ledgerCashFlowSectionsFromRollup(ledgerRows[0])) : ledgerCashFlowSections(actuals)}
            currentPeriodLabel={ledgerBuckets.currentLabel}
            currentPeriodProof={ledgerBuckets.currentState}
            sourceState={sourceState}
            ledgerAggregateRowProof
            scenario={scenario}
            setScenario={setScenario}
            forceOpen={activeSection === 'formal'}
          />
        </>
      )}
    </div>
  );
}

function hasSelectedLedgerScope(scenario) {
  const sourceState = scenario?._ledgerSourceState || null;
  const bookScope = sourceState?.bookScope || null;
  return Boolean(
    sourceState?.hasActiveScope ||
    sourceState?.hasActiveBook ||
    sourceState?.hasExplicitBook ||
    bookScope?.bookId ||
    bookScope?.shelfId
  );
}

function ActualsZeroScopeShell({ activeSection, onBackToOverview }) {
  const show = makeShowBlock(activeSection);
  const cards = [
    ['Assets', 0],
    ['Liabilities', 0],
    ['Equity', 0],
    ['Income', 0],
    ['Cash flow', 0],
  ];
  return (
    <div className={TAB_CONTENT} data-ux-pattern="actuals-zero-scope-shell">
      <SubViewBreadcrumb moduleId="actuals" activeSection={activeSection} onBack={onBackToOverview} />
      {show('overview') && (
        <SectionCard
          sectionId="overview"
          title="Actuals held until Book/Shelf selection"
          subtitle="No selected Book or Shelf scope. Static demo, snapshot, and synthetic actuals stay blocked in the private app."
        >
          <div className={KPI_ROW_COMPACT}>
            {cards.map(([label, value]) => (
              <KPICard
                key={label}
                label={label}
                value={<MoneyCell value={value} label={`${label} held at zero`} />}
                sub="0 source rows"
                provenance="warning"
                provenanceTip="Select a Book or Shelf before loading private Ledger actuals."
              />
            ))}
          </div>
        </SectionCard>
      )}
      {show('overview', 'equity') && (
        <HeldEquitySection subtitle="No selected Book or Shelf scope. Equity stays at zero until Ledger actuals are loaded." />
      )}
      {show('formal') && (
        <HeldFormalStatements subtitle="No selected Book or Shelf scope. Formal statements render as a zero hold shell with no bundled/static/private amounts." />
      )}
      {show('comparative') && (
        <HeldComparativeSection subtitle="No selected Book or Shelf scope. Comparative periods render as zero hold columns only." />
      )}
    </div>
  );
}

function ActualsLedgerHoldShell({ scenario, activeSection, onBackToOverview }) {
  const show = makeShowBlock(activeSection);
  const sourceState = scenario?._ledgerSourceState || null;
  const reason = sourceState?.fallbackReason || sourceState?.warning || 'Selected Ledger scope has not emitted actuals yet.';
  return (
    <div className={TAB_CONTENT} data-ux-pattern="actuals-ledger-hold-shell">
      <SubViewBreadcrumb moduleId="actuals" activeSection={activeSection} onBack={onBackToOverview} />
      {show('overview') && (
        <SectionCard
          sectionId="overview"
          title="Actuals waiting on Ledger"
          subtitle="A Book or Shelf scope is selected, but Ledger-derived actuals are not ready. Static demo and synthetic rows stay blocked."
        >
          <p className={HINT}>{reason}</p>
        </SectionCard>
      )}
      {show('overview', 'equity') && (
        <HeldEquitySection
          subtitle="Selected scope is waiting on Ledger actuals. Equity stays at zero; no static or synthetic fill is used."
          proof="ledger-hold"
        />
      )}
      {show('formal') && (
        <HeldFormalStatements
          subtitle="Selected scope is waiting on Ledger actuals. Formal statements stay in hold mode with zero values only."
          sourceState={sourceState}
        />
      )}
      {show('comparative') && (
        <HeldComparativeSection subtitle="Selected scope is waiting on Ledger actuals. Comparative periods stay at zero until source rows arrive." />
      )}
    </div>
  );
}

export default function ActualsTab({ scenario, setScenario, onJump, activeSection, onBackToOverview }) {
  const show = makeShowBlock(activeSection);

  /* Proposal 3 (2026-04-25) — sub-tab system removed alongside the JE
     editor migration to FCC. ActualsTab is now overview-only with section
     anchors. Sidebar children scroll into view; no internal tab switching. */
  useEffect(() => {
    if (!activeSection) return;
    const t = setTimeout(() => {
      if (typeof document === 'undefined') return;
      const el = document.getElementById(`section-${activeSection}`) || document.getElementById(activeSection);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.remove('section-focused');
      void el.offsetWidth;
      el.classList.add('section-focused');
      setTimeout(() => el.classList.remove('section-focused'), 1900);
    }, 60);
    return () => clearTimeout(t);
  }, [activeSection]);

  if (scenario?._ledgerActuals) {
    return (
      <LedgerActualsSurface
        scenario={scenario}
        setScenario={setScenario}
        activeSection={activeSection}
        onBackToOverview={onBackToOverview}
      />
    );
  }

  if (scenario?._allowStaticDemoActuals !== true) {
    return hasSelectedLedgerScope(scenario)
      ? <ActualsLedgerHoldShell scenario={scenario} activeSection={activeSection} onBackToOverview={onBackToOverview} />
      : <ActualsZeroScopeShell activeSection={activeSection} onBackToOverview={onBackToOverview} />;
  }

  const assetsLiquid = totalAssetsLiquid();
  const ccDebt = totalCreditCardDebt();
  const ccCredits = totalCCCredits();
  const ltl = totalLongTermLiabilities();
  const eq = totalEquity();

  const fixedAssetsNet = totalFixedAssetsNet();
  const ccDebtV72      = totalCCDebtFromBlocks();
  const ccCreditsV72   = totalCCCreditsFromBlocks();
  const ltlV72         = totalLTLFromBlocks();

  const actualsPeriods = normalizePeriodScalar(scenario.formalPeriodCount, 12);
  const actualsDensity = normalizePeriodScalar(scenario.formalPeriodDensity, 1);
  const actualsHorizon = actualsPeriods * actualsDensity;
  const actualsDirection = getTabDirection(scenario, 'actuals');
  const actualsProjection = useMemo(() => {
    if (actualsPeriods <= 1) return null;
    const raw = buildThreeStatementForDirection(scenario, actualsDirection, actualsHorizon, actualsDensity);
    const forecast         = aggregateByDensity(raw.forecast,         actualsDensity, 'last');
    const incomeStatements = aggregateByDensity(raw.incomeStatements, actualsDensity, 'sum');
    const balanceSheets    = aggregateByDensity(raw.balanceSheets,    actualsDensity, 'last');
    const cashFlows        = aggregateByDensity(raw.cashFlows,        actualsDensity, 'sum');
    const periodLabels     = forecast.map(r => fmtPeriod(r.ym ?? r.label, actualsDensity));
    const currentState     = currentPeriodStateForRows(forecast, actualsDensity);
    const currentLabel     = currentState.label;
    return { forecast, incomeStatements, balanceSheets, cashFlows, periodLabels, currentLabel, currentState };
  }, [scenario, actualsPeriods, actualsDensity, actualsHorizon, actualsDirection]);
  const actualsPeriodLabels = actualsProjection?.periodLabels;
  const actualsCurrentLabel = actualsProjection?.currentLabel ?? fmtPeriod(currentYM(), actualsDensity);
  const actualsCurrentProof = actualsProjection?.currentState ?? null;
  const actualsDirLabel = actualsDirection === 'past' ? 'trailing' : actualsDirection === 'future' ? 'leading' : 'spanning';
  const actualsLedgerSourceState = scenario?._ledgerSourceState || null;
  const nw = netWorth();
  const susTotal = totalSuspense();
  const is = buildIncomeStatement(scenario);
  const formal = scenario.statementStyle === 'formal';
  const transpose = scenario.tableOrientation === 'vertical';

  const sparkLiquid = kpiSparkSeries('liquid', scenario);
  const sparkCC     = kpiSparkSeries('ccDebt', scenario);
  const sparkNetFCF = kpiSparkSeries('netFCF', scenario);

  const bankBreakdown = BALANCES.bank.map((a) => ({ label: a.name, amount: a.amount, source: a.note, cat: 'Bank' }));
  const cashBreakdown = BALANCES.cash.map((a) => ({ label: a.name, amount: a.amount, source: a.note, cat: 'Cash' }));
  const fixedAssetBreakdown = FIXED_ASSETS
    .filter((a) => a.status !== 'not-yet-acquired')
    .map((a) => ({
      label: a.name,
      amount: Math.max(0, (a.currentValue || 0) - (a.accumDepreciation || 0)),
      source: a.note,
      cat: a.category || 'Non-current',
    }));
  const liquidBreakdown = [...bankBreakdown, ...cashBreakdown];
  const totalAssetsAll = assetsLiquid + fixedAssetsNet;

  const ccDebtBreakdown = BALANCES.creditCards
    .filter(c => c.amount < 0)
    .map((a) => ({ label: a.name, amount: Math.abs(a.amount), source: a.note, cat: 'Credit Card' }));
  const ltlBreakdown = BALANCES.longTermLiabilities.map((a) => ({
    label: a.name, amount: Math.abs(a.amount), source: a.note, cat: 'Long-term',
  }));

  const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const allAssetRows = [
    ...BALANCES.bank.map((a, i) => ({ id: `b${i}`, accountId: `asset.bank.${slug(a.name)}`,
      _coaTier: a.dust === true ? 'dust' : 'secondary',
      name: a.name, amount: a.amount, note: a.note, cat: 'Bank' })),
    ...BALANCES.cash.map((a, i) => ({ id: `c${i}`, accountId: `asset.cash.${slug(a.name)}`,
      _coaTier: a.dust === true ? 'dust' : 'secondary',
      name: a.name, amount: a.amount, note: a.note, cat: 'Cash' })),
    ...FIXED_ASSETS
      .filter((a) => a.status !== 'not-yet-acquired')
      .map((a, i) => ({
        id: `fa${i}`,
        accountId: `asset.fixed.${slug(a.name)}`,
        _coaTier: 'primary',
        name: a.name,
        amount: Math.max(0, (a.currentValue || 0) - (a.accumDepreciation || 0)),
        note: a.note,
        cat: a.category || 'Non-current',
      })),
  ];
  const allLiabRows = [
    ...BALANCES.creditCards.map((a, i) => ({ id: `cc${i}`, accountId: `liability.cc.${slug(a.name)}`,
      _coaTier: 'secondary',
      name: a.name, amount: a.amount, note: a.note, cat: 'Credit Card' })),
    ...BALANCES.longTermLiabilities.map((a, i) => ({ id: `lt${i}`, accountId: `liability.lt.${slug(a.name)}`,
      _coaTier: 'primary',
      name: a.name, amount: a.amount, note: a.note, cat: 'Long-term' })),
  ];
  const allEqRows = BALANCES.equity.map((a, i) => ({ id: `eq${i}`, accountId: `equity.${slug(a.name)}`,
    _coaTier: 'primary',
    name: a.name, amount: a.amount, note: a.note, cat: 'Equity' }));

  const visibleByCoa = (rows) => rows.filter(r =>
    resolveAccountVisibility({ id: r.accountId, tier: r._coaTier }, scenario, 'actuals')
  );
  const assetRows = visibleByCoa(allAssetRows);
  const liabRows  = visibleByCoa(allLiabRows);
  const eqRows    = visibleByCoa(allEqRows);
  const hiddenAssets = allAssetRows.length - assetRows.length;
  const hiddenLiabs  = allLiabRows.length  - liabRows.length;
  const hiddenEquity = allEqRows.length    - eqRows.length;

  const isRows = [
    { id: 'rev-sal',  cat: 'Revenue',  name: 'Primary income (gross)',  amount:  is.revenue },
    { id: 'rev-fvf',  cat: 'Revenue',  name: 'Recurring revenue (current month)', amount:  is.fvf, __breakdown: [], __registryKey: 'fvfMRR' },
    { id: 'exp-subs', cat: 'Expense',  name: 'Subscriptions (all recurring)', amount: -is.subsExpense, __breakdown: recurringBreakdown(null, scenario).map(r => ({ ...r, amount: -r.amount })), __registryKey: 'recurringSubs' },
    { id: 'exp-fam',  cat: 'Expense',  name: 'Direct operating AP',     amount: -is.familyDirect, __registryKey: 'familyDirect' },
    { id: 'exp-mar',  cat: 'Expense',  name: 'Routed operating AP',     amount: -is.marianExpense, __registryKey: 'marianAP' },
    { id: 'exp-int',  cat: 'Expense',  name: 'Term debt interest',      amount: -is.mortgagesInterestPortion, __registryKey: 'mortgages' },
    { id: 'exp-wht',  cat: 'Expense',  name: 'Withholding / estimated tax', amount: -is.wht },
    { id: 'exp-gov',  cat: 'Expense',  name: 'Statutory contributions + fees', amount: -is.gov },
    { id: 'net',      cat: 'Net',      name: 'Net Income',              amount:  is.netIncome, __rowClass: 'row-total' },
  ];

  const features = getModuleFeatures(scenario, 'actuals');

  if (activeSection === 'comparative') {
    return (
      <div className={TAB_CONTENT}>
        <SubViewBreadcrumb moduleId="actuals" activeSection={activeSection} onBack={onBackToOverview} />
        <ComparativeTablesView scenario={scenario} moduleId="actuals" />
      </div>
    );
  }

  return (
    <div className={TAB_CONTENT}>
      <SubViewBreadcrumb moduleId="actuals" activeSection={activeSection} onBack={onBackToOverview} />
      {show('overview') && (
        <ModuleSettingsPanel
          scenario={scenario}
          setScenario={setScenario}
          moduleId="actuals"
          title="Actuals · module settings"
          features={[
            { key: 'balanceSheetDetail', label: 'Balance Sheet detail', hint: 'Assets / Liabilities / Equity nested blocks' },
            { key: 'incomeStatement',    label: 'Income Statement',     hint: 'Current-month estimate + filing view' },
            { key: 'primaryIncome',      label: 'Primary income',       hint: 'Baseline income cadence and contribution assumptions' },
            { key: 'formalStatements',   label: 'Formal statements',    hint: 'IS / BS / CF — period × density × direction' },
          ]}
        />
      )}

      {/* Top KPI strip — overview-only; sub-view focus modes hide it.
          Proposal 8 Tier 2 — every card carries a provenance chip so the
          source class is visible at a glance. Most Actuals KPIs read the
          ledger snapshot; primary income reads a schedule; net worth is computed. */}
      {show('overview') && (
      <div className={KPI_ROW}>
        <KPICard
          label="Net Worth (proxy)"
          value={<MoneyCell value={nw} tone={nw >= 0 ? 'good' : 'bad'} term="netWorth" registryKey="netWorth" onJump={onJump} label="Net Worth (proxy)" />}
          sub="assets - short-term debt - LT liabilities"
          tone={nw >= 0 ? 'good' : 'bad'}
          spark={<KPISparkline series={sparkNetFCF} betterWhen="higher" semanticTone={!!scenario.sparklineSemanticTone} gradient={!!scenario.sparklineGradient} tone={nw >= 0 ? 'good' : 'bad'} />}
          provenance="computed"
          provenanceTip="Sum of ledger amounts: Liquid Assets + Fixed (book) - short-term debt - long-term liabilities. Suspense / reconciliation entries excluded - for the audited number use Total Equity on the Balance Sheet."
        />
        <KPICard
          label="Liquid Assets"
          value={<MoneyCell value={assetsLiquid} breakdown={liquidBreakdown} registryKey="liquidAssets" term="liquidAssets" onJump={onJump} label="Liquid Assets" />}
          sub={`${BALANCES.bank.length} bank + ${BALANCES.cash.length} cash`}
          spark={<KPISparkline series={sparkLiquid} betterWhen="higher" semanticTone={!!scenario.sparklineSemanticTone} gradient={!!scenario.sparklineGradient} tone="good" />}
          provenance="ledger"
          provenanceTip={`Sum of every liquid bank + cash + e-wallet balance from BALANCES (snapshot ${BALANCES.bank.length + BALANCES.cash.length} accounts). Maintaining-balance / dust-tier accounts excluded by default — toggle in Settings → Account Visibility.`}
        />
        <KPICard
          label="Short-term Debt"
          value={<MoneyCell value={ccDebt} breakdown={ccDebtBreakdown} registryKey="ccDebt" term="ccDebt" onJump={onJump} tone="bad" label="Short-term Debt" />}
          sub={`${BALANCES.creditCards.filter(c => c.amount < 0).length} revolving lines`}
          tone="bad"
          spark={<KPISparkline series={sparkCC} betterWhen="lower" semanticTone={!!scenario.sparklineSemanticTone} gradient={!!scenario.sparklineGradient} tone="bad" />}
          provenance="ledger"
          provenanceTip="Sum of negative revolving balances from BALANCES. Positive credit balances are excluded - those flow to Card Credits."
        />
        <KPICard
          label="Card Credits"
          value={<MoneyCell value={ccCredits} tone="good" term="ccCredits" registryKey="ccCredits" onJump={onJump} label="Credit Card Credits" />}
          sub="positive balances / statement credit"
          tone="good"
          provenance="ledger"
          provenanceTip="Sum of positive credit-card balances (statement credits / overpayments) from BALANCES."
        />
        <KPICard
          label="Long-term Liab."
          value={<MoneyCell value={ltl} breakdown={ltlBreakdown} term="ltl" registryKey="ltl" onJump={onJump} label="Long-term Liabilities" />}
          sub="term debt + AP + tax"
          provenance="ledger"
          provenanceTip="Term debt, long-term AP, and tax liabilities from BALANCES.longTermLiabilities. Add piecewise outstanding pins via Settings -> Piecewise (LTLs section)."
        />
        <KPICard
          label="Monthly Net Primary Income"
          value={<MoneyCell value={calcMonthlyNet()} term="salaryNet" registryKey="salaryNet" onJump={onJump} label="Monthly net primary income" />}
          sub={`Fortnight: ${fmtMoney(calcFortnightlyGross() - calcMonthlyWHT('mid') / 2 - calcMonthlyGovContribs() / 2)}`}
          provenance="schedule"
          provenanceTip="Computed from the primary-income schedule: hourly x hours/month - withholding tax - statutory contributions. Schedule is editable in Actuals · Primary income."
        />
      </div>
      )}

      {/* Balance Sheet — three collapsible nested sections */}
      {(() => {
        const topAssets = [...assetRows].sort((a, b) => b.amount - a.amount).slice(0, 3);
        const topLiabs  = [...liabRows].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)).slice(0, 3);
        const topEquity = [...eqRows].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)).slice(0, 3);
        const totalLiabs = -(ccDebt + ltl - ccCredits);

        return (
          <>
            {show('overview', 'assets') && features.balanceSheetDetail && (
            <NestedSection
              sectionId="assets"
              title={
                <span>
                  Assets
                  {isAccountVisibilityCustomized(scenario) && <ModifiedBadge active reason={OVERRIDE_REASON.accountVis} />}
                </span>
              }
              subtitle={`Imported ledger snapshot as of 2026-04-19${hiddenAssets ? ` · ${hiddenAssets} folded (toggle in Settings → Account Visibility)` : ''}`}
              scenario={scenario}
              setScenario={setScenario}
              headline={
                <div className={KPI_ROW_COMPACT}>
                  <KPICard label="Total assets" value={<MoneyCell value={totalAssetsAll} tone="good" term="liquidAssets" registryKey="liquidAssets" onJump={onJump} label="Total assets" />} sub={`${assetRows.length} accounts`} provenance="ledger" provenanceTip="Sum of every visible current and non-current asset row, respecting account-visibility filters." />
                  <KPICard label="Bank" value={<MoneyCell value={BALANCES.bank.reduce((s, a) => s + a.amount, 0)} term="liquidAssets" label="Bank balances" />} sub={`${BALANCES.bank.length} accounts`} provenance="ledger" provenanceTip="Sum of every BALANCES.bank balance in the demo snapshot." />
                  <KPICard label="Cash" value={<MoneyCell value={BALANCES.cash.reduce((s, a) => s + a.amount, 0)} term="liquidAssets" label="Cash + e-wallets" />} sub={`${BALANCES.cash.length} accounts`} provenance="ledger" provenanceTip="Sum of every BALANCES.cash balance — physical cash + e-wallets (PayPal, Wise, etc.)." />
                  <KPICard label="Non-current" value={<MoneyCell value={fixedAssetsNet} term="fixedAssets" label="Non-current assets" breakdown={fixedAssetBreakdown} />} sub={`${fixedAssetBreakdown.length} assets`} provenance="ledger" provenanceTip="Net carrying value of fixed and non-current assets in the model snapshot." />
                </div>
              }
              topDrivers={
                <div>
                  {topAssets.map((r) => (
                    <div className={DRIVER_ROW} key={r.id}>
                      <div>
                        <span className="text-text">{r.name}</span>
                        <span className="text-text-dim text-[0.75rem]"> · {r.cat}</span>
                      </div>
                      <span className="text-text tabular-nums font-semibold"><MoneyCell value={r.amount} /></span>
                    </div>
                  ))}
                </div>
              }
              fullCount={assetRows.length}
              fullList={
                <DataTable
                  compact
                  columns={[
                    { key: 'name',   label: 'Account', width: '42%' },
                    { key: 'cat',    label: 'Type',    width: '14%' },
                    { ...moneyCellCol('amount'), label: 'Amount', width: '20%' },
                    { key: 'note',   label: 'Note' },
                  ]}
                  rows={assetRows}
                  footer={{ name: 'Total assets', cat: '', amount: totalAssetsAll, note: '' }}
                />
              }
            />
            )}

            {show('overview', 'liabilities') && features.balanceSheetDetail && (
            <NestedSection
              sectionId="liabilities"
              title="Liabilities"
              subtitle={`Current liabilities, long-term debt, payables, and obligations${hiddenLiabs ? ` · ${hiddenLiabs} folded` : ''}`}
              scenario={scenario}
              setScenario={setScenario}
              headline={
                <div className={KPI_ROW_COMPACT}>
                  <KPICard label="Total liabilities" value={<MoneyCell value={totalLiabs} tone="bad" term="ltl" label="Total liabilities" />} sub={`${liabRows.length} lines`} provenance="computed" provenanceTip="Sum of short-term debt + long-term liabilities minus credits. Net liabilities side of the balance sheet." />
                  <KPICard label="Short-term debt" value={<MoneyCell value={ccDebt} tone="bad" term="ccDebt" registryKey="ccDebt" onJump={onJump} label="Short-term debt" />} sub={`${BALANCES.creditCards.filter(c => c.amount < 0).length} revolving lines`} provenance="ledger" provenanceTip="Sum of negative card or revolving balances from BALANCES.creditCards." />
                  <KPICard label="Long-term" value={<MoneyCell value={ltl} term="ltl" registryKey="ltl" onJump={onJump} label="Long-term Liabilities" />} sub={`${BALANCES.longTermLiabilities.length} debt/AP lines`} provenance="ledger" provenanceTip="Term debt, long-term AP, and tax liabilities from BALANCES.longTermLiabilities." />
                </div>
              }
              topDrivers={
                <div>
                  {topLiabs.map((r) => (
                    <div className={DRIVER_ROW} key={r.id}>
                      <div>
                        <span className="text-text">{r.name}</span>
                        <span className="text-text-dim text-[0.75rem]"> · {r.cat}</span>
                      </div>
                      <span className="text-text tabular-nums font-semibold"><MoneyCell value={r.amount} tone={r.amount < 0 ? 'bad' : undefined} /></span>
                    </div>
                  ))}
                </div>
              }
              fullCount={liabRows.length}
              fullList={
                <DataTable
                  compact
                  columns={[
                    { key: 'name',   label: 'Account', width: '42%' },
                    { key: 'cat',    label: 'Type',    width: '14%' },
                    { ...moneyCellCol('amount'), label: 'Amount', width: '20%' },
                    { key: 'note',   label: 'Note' },
                  ]}
                  rows={liabRows}
                  footer={{ name: 'Total liabilities', cat: '', amount: totalLiabs, note: '' }}
                />
              }
            />
            )}

            {show('overview', 'equity') && features.balanceSheetDetail && (
            <NestedSection
              sectionId="equity"
              title="Equity"
              subtitle={`Suspense balance: ${fmtMoney(susTotal)} — ghost outflows to chase${hiddenEquity ? ` · ${hiddenEquity} folded` : ''}`}
              scenario={scenario}
              setScenario={setScenario}
              headline={
                <div className={KPI_ROW_COMPACT}>
                  <KPICard label="Total equity" value={<MoneyCell value={eq} tone={eq >= 0 ? 'good' : 'bad'} term="equity" label="Total equity" />} sub={`${eqRows.length} lines`} />
                  <KPICard label="Suspense" value={<MoneyCell value={susTotal} tone="warn" term="suspense" label="Suspense balance" />} sub="ghost outflows" />
                </div>
              }
              topDrivers={
                <div>
                  {topEquity.map((r) => (
                    <div className={DRIVER_ROW} key={r.id}>
                      <div>
                        <span className="text-text">{r.name}</span>
                        <span className="text-text-dim text-[0.75rem]"> · {r.cat}</span>
                      </div>
                      <span className="text-text tabular-nums font-semibold"><MoneyCell value={r.amount} /></span>
                    </div>
                  ))}
                </div>
              }
              fullCount={eqRows.length}
              fullList={
                <DataTable
                  compact
                  columns={[
                    { key: 'name',   label: 'Account', width: '42%' },
                    { key: 'cat',    label: 'Type',    width: '14%' },
                    { ...moneyCellCol('amount'), label: 'Amount', width: '20%' },
                    { key: 'note',   label: 'Note' },
                  ]}
                  rows={eqRows}
                  footer={{ name: 'Total equity', cat: '', amount: eq, note: '' }}
                />
              }
            />
            )}
          </>
        );
      })()}

      {/* Income Statement — gated so the "Income Statement" sidebar child
          focus-mode-isolates this section. */}
      {show('overview', 'income') && features.incomeStatement && (formal ? (
        <SectionCard
          sectionId="income"
          title="Income Statement — filing style"
          subtitle={`Entity: Current Lens Entity · ${fmtDate(new Date())} · parenthesized negatives`}
        >
          <FormalStatementTable
            entity="Current Lens Entity"
            title="Statement of Income — Accrual Basis"
            period="For the month ending 2026-04-30"
            sections={shapeIncomeStatement(is)}
            sourceState={actualsLedgerSourceState}
            ledgerAggregateRowProof
          />
        </SectionCard>
      ) : (
        <SectionCard
          sectionId="income"
          title="Income Statement — current-month estimate"
          subtitle="Accrual-basis proxy; recurring revenue is 0 until start month. Hover the Subscriptions amount to see every line."
        >
          <DataTable
            transpose={transpose}
            columns={[
              { key: 'cat',    label: 'Class',  width: '15%' },
              { key: 'name',   label: 'Line',   width: '55%' },
              {
                key: 'amount',
                label: 'Amount',
                align: 'right',
                format: (v, row) => (
                  <MoneyCell
                    value={v}
                    breakdown={row.__breakdown && row.__breakdown.length > 0 ? row.__breakdown : undefined}
                    registryKey={row.__registryKey}
                    onJump={onJump}
                  />
                ),
              },
            ]}
            rows={isRows}
          />
        </SectionCard>
      ))}

      {show('overview', 'income') && features.incomeStatement && formal && (
        <SectionCard
          title="Balance Sheet — filing style"
          subtitle={`Entity: Current Lens Entity · As of 2026-04-19 · parenthesized negatives`}
        >
          <FormalStatementTable
            entity="Current Lens Entity"
            title="Statement of Financial Position"
            period="As of 2026-04-19"
            sections={shapeBalanceSheet({
              assetsLiquid,
              fixedAssetsNet,
              ccDebt:    ccDebtV72,
              ccCredits: ccCreditsV72,
              ltl:       ltlV72,
              eq, susTotal,
            })}
            sourceState={actualsLedgerSourceState}
            ledgerAggregateRowProof
          />
        </SectionCard>
      )}

      {/* Primary income mini spec */}
      {show('overview', 'primary-income') && features.primaryIncome && (
      <SectionCard sectionId="primary-income" title="Primary Income" subtitle={SALARY.scheduleNote}>
        <div className={KPI_ROW}>
          <KPICard label="Hourly" value={`$${SALARY.rateHourly}`} sub={`${SALARY.hoursPerDay}h x ${SALARY.workingDaysPerFortnight} days`} />
          <KPICard label="Cadence gross" value={<MoneyCell value={calcFortnightlyGross()} term="salaryGross" label="Cadence gross" />} />
          <KPICard label="Monthly primary gross" value={<MoneyCell value={calcMonthlyGross()} term="salaryGross" registryKey="salaryGross" onJump={onJump} label="Monthly primary gross" />} />
          <KPICard
            label="Monthly tax + statutory"
            value={<MoneyCell value={calcMonthlyWHT() + calcMonthlyGovContribs()} label="Withholding tax + statutory contributions" />}
            sub={fmtPct((calcMonthlyWHT() + calcMonthlyGovContribs()) / calcMonthlyGross())}
          />
          <KPICard
            label="Monthly term debt"
            value={<MoneyCell value={calcMonthlyMortgages()} term="monthlyMortgages" registryKey="monthlyMortgages" onJump={onJump} label="Monthly term-debt total" />}
            sub={`${MORTGAGES.length} loans — imported bill snapshot`}
          />
        </div>
      </SectionCard>
      )}

      {/* Formal Financial Statements — full 3-statement, gated by 'formal' sidebar child. */}
      {show('overview', 'formal') && features.formalStatements && (<>
      <FormalStatementSection
        tabId="actuals" stmtKey="is"
        label="Income Statement (formal)"
        subtitle={`Accrual basis · ${actualsPeriods > 1 ? `${actualsPeriods}-period ${actualsDirLabel} (synthetic local horizon until provider proof)` : `Net income: ${fmtMoney(is.netIncome, { decimals: 0 })}`}`}
        title="Statement of Income — Accrual Basis"
        entity="Current Lens Entity"
        period={actualsPeriods > 1 ? `${actualsPeriodLabels[0]} – ${actualsPeriodLabels[actualsPeriodLabels.length - 1]}` : `For the month ending ${currentYM()}-30`}
        periods={actualsPeriods > 1 ? actualsPeriodLabels : undefined}
        sections={actualsPeriods > 1 ? shapeIncomeStatementMulti(actualsProjection.incomeStatements) : shapeIncomeStatement(is)}
        currentPeriodLabel={actualsCurrentLabel}
        currentPeriodProof={actualsCurrentProof}
        sourceState={actualsLedgerSourceState}
        ledgerAggregateRowProof
        scenario={scenario} setScenario={setScenario}
        forceOpen={activeSection === 'formal'}
      />
      <FormalStatementSection
        tabId="actuals" stmtKey="bs"
        label="Balance Sheet (formal)"
        subtitle={actualsPeriods > 1
          ? `${actualsPeriods}-period ${actualsDirLabel} · A = L + E (synthetic local horizon until provider proof)`
          : `A = L + E · Assets ${fmtMoney(assetsLiquid + fixedAssetsNet, { decimals: 0 })} · Liab ${fmtMoney(ccDebtV72 - ccCreditsV72 + ltlV72, { decimals: 0 })}`}
        title="Statement of Financial Position"
        entity="Current Lens Entity"
        period={actualsPeriods > 1 ? `${actualsPeriodLabels[0]} – ${actualsPeriodLabels[actualsPeriodLabels.length - 1]}` : `As of ${currentYM()}-30`}
        periods={actualsPeriods > 1 ? actualsPeriodLabels : undefined}
        sections={actualsPeriods > 1
          ? shapeBalanceSheetMulti(actualsProjection.balanceSheets)
          : shapeBalanceSheet({
              assetsLiquid, fixedAssetsNet,
              ccDebt: ccDebtV72, ccCredits: ccCreditsV72, ltl: ltlV72,
              eq, susTotal,
            })}
        currentPeriodLabel={actualsCurrentLabel}
        currentPeriodProof={actualsCurrentProof}
        sourceState={actualsLedgerSourceState}
        ledgerAggregateRowProof
        scenario={scenario} setScenario={setScenario}
        forceOpen={activeSection === 'formal'}
      />
      <FormalStatementSection
        tabId="actuals" stmtKey="cf"
        label="Cash Flow Statement (formal)"
        subtitle={actualsPeriods > 1
          ? `${actualsPeriods}-period ${actualsDirLabel} · CFO + CFI + CFF (synthetic local horizon until provider proof)`
          : 'CFO + CFI + CFF = Net change in cash · single-month snapshot'}
        title="Statement of Cash Flows"
        entity="Current Lens Entity"
        period={actualsPeriods > 1 ? `${actualsPeriodLabels[0]} – ${actualsPeriodLabels[actualsPeriodLabels.length - 1]}` : `For the month ending ${currentYM()}-30`}
        periods={actualsPeriods > 1 ? actualsPeriodLabels : undefined}
        sections={actualsPeriods > 1
          ? shapeCashFlowStatementMulti(actualsProjection.cashFlows)
          : shapeCashFlowStatement(buildCashFlowSnapshot(scenario, currentYM()))}
        currentPeriodLabel={actualsCurrentLabel}
        currentPeriodProof={actualsCurrentProof}
        sourceState={actualsLedgerSourceState}
        ledgerAggregateRowProof
        scenario={scenario} setScenario={setScenario}
        forceOpen={activeSection === 'formal'}
      />
      </>)}
    </div>
  );
}
